import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Linkedin, Mail, MapPin, Heart, Zap, Users, Compass } from "lucide-react";

const TEAM = [
  {
    name: "Juan Arroyo",
    role: "CEO & Fundador",
    bio: "Abogado y emprendedor con visión estratégica para transformar el mercado inmobiliario comercial. Lidera Bizaclia con dirección ejecutiva clara, combinando rigor jurídico con instinto empresarial para generar resultados concretos y escalables.",
    initials: "JA",
    linkedin: "https://www.linkedin.com/in/juan-arroyo-5a97996/",
    email: "info@esfericgroup.com",
  },
  {
    name: "Rodny Abreu",
    role: "CTO",
    bio: "Co-fundador de wedoprogress.com y blackbase.io. Ingeniero con profunda pasión por la tecnología y la arquitectura de sistemas. Diseña la infraestructura de Bizaclia para garantizar velocidad, escalabilidad y una experiencia robusta.",
    initials: "RA",
    linkedin: "https://www.linkedin.com/in/rodny-abreu/",
    email: "rodny@wedoprogress.com",
  },
  {
    name: "Olivia Fernández",
    role: "Head of Design",
    bio: "Co-fundadora de wedoprogress.com y blackbase.io. Diseñadora con un enfoque implacable en la experiencia de usuario y la identidad visual. Da forma a cada interacción de Bizaclia para que sea intuitiva, elegante y funcional.",
    initials: "OF",
    linkedin: "https://www.linkedin.com/in/olivia-fern%C3%A1ndez-043102360/",
    email: "olivia@wedoprogress.com",
  },
];

const VALUES = [
  { icon: Compass, title: "Autonomía con soporte", body: "Cada miembro tiene libertad para proponer, liderar y ejecutar. El equipo está cuando necesitas feedback, no cuando no lo pides." },
  { icon: Zap, title: "Iteración rápida", body: "Preferimos lanzar, medir y ajustar a esperar meses por la versión perfecta. La velocidad es ventaja competitiva." },
  { icon: Users, title: "Decisiones basadas en datos", body: "Opiniones bien argumentadas, pero métricas que deciden. Cultura de aprender de lo que falla y celebrar lo que funciona." },
  { icon: Heart, title: "Impacto real", body: "No construimos features por construir. Cada mejora resuelve un problema real de un propietario, inversor o agente." },
];

export const Route = createFileRoute("/equipo")({
  head: () => ({
    meta: [
      { title: "Equipo — Bizaclia.com" },
      { name: "description", content: "Conoce al equipo que hace posible Bizaclia. Profesionales apasionados por transformar el mercado inmobiliario comercial." },
      { property: "og:title", content: "Equipo — Bizaclia.com" },
      { property: "og:description", content: "Conoce al equipo que hace posible Bizaclia." },
    ],
  }),
  component: EquipoPage,
});

function EquipoPage() {
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div
          aria-hidden
          className="absolute inset-x-0 -top-32 h-[520px] bg-[radial-gradient(ellipse_at_top,theme(colors.primary/15%),transparent_60%)] pointer-events-none"
        />
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio
          </Link>

          <div className="max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              El equipo
            </span>
            <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05]">
              Gente que entiende{" "}
              <span className="text-primary">negocios</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Somos un equipo multidisciplinar unido por una obsesión: hacer que el mercado
              inmobiliario comercial sea accesible, transparente y eficiente para todos.
            </p>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 sm:py-24 bg-surface border-y border-border">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Dirección
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Quien está al mando
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TEAM.map((member) => (
              <div
                key={member.name}
                className="rounded-3xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary font-display font-bold text-lg">
                    {member.initials}
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">
                      {member.name}
                    </h3>
                    <p className="text-sm text-primary font-medium">{member.role}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  {member.bio}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border hover:border-primary/40 hover:text-primary transition"
                    aria-label={`LinkedIn de ${member.name}`}
                  >
                    <Linkedin className="h-4 w-4" />
                  </a>
                  <a
                    href={`mailto:${member.email}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border hover:border-primary/40 hover:text-primary transition"
                    aria-label={`Email de ${member.name}`}
                  >
                    <Mail className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture / Values */}
      <section className="py-20 sm:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Cultura
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Cómo trabajamos
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {VALUES.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-3xl border border-border bg-card p-6 hover:border-primary/40 transition"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-soft">
                    <Icon className="h-5 w-5 text-primary" />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-lg tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location + Careers CTA */}
      <section className="py-20 sm:py-24 bg-surface border-y border-border">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                Dónde estamos
              </span>
              <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
                Sant Cugat del Vallès, Barcelona
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Nuestra sede está en Sant Cugat del Vallès, a pocos minutos de Barcelona.
                Desde aquí operamos con alcance nacional, combinando la tranquilidad de la
                periferia con la energía de un hub empresarial dinámico.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary" />
                BIZACLIA SL — Avda. Más Fuster 27, 08197 Sant Cugat del Vallès, Barcelona
              </div>
            </div>

            <div className="rounded-3xl bg-ink text-ink-foreground p-8 sm:p-10 relative overflow-hidden">
              <div aria-hidden className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Únete al equipo
                </span>
                <h3 className="mt-4 font-display font-bold text-2xl tracking-tight">
                  ¿Quieres construir el futuro del inmueble comercial?
                </h3>
                <p className="mt-3 text-ink-foreground/70">
                  Buscamos personas ambiciosas, pragmáticas y obsesionadas con el impacto.
                  Si crees que puedes aportar, escríbenos.
                </p>
                <div className="mt-6">
                  <Link
                    to="/mybizaclia"
                    search={{ tab: "soporte" }}
                    className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition"
                  >
                    Contactar con nosotros
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
